import api from './api';
import { saveBlob } from '../utils/saveFile';

/**
 * EDI Pending Invoices (Accounting) — the bucket list + one bucket's invoices.
 * Backed by app/api/api_v1/endpoints/pending_edi_invoices.py (both GET, read-only).
 * See pages/accounting/PendingEdiInvoices.tsx.
 *
 * Replaces V1's Accounting-dashboard "EDI Pending Invoice" panel and the Send EDI
 * screen it opened. Read-only for now: sending is the next phase.
 */

/** V1 paints Internal above Kleinschmidt; the API returns them in that order. */
export type EdiInvoiceBucketGroup = 'internal' | 'kleinschmidt';

export interface EdiInvoiceBucket {
  /** edihandle for a Kleinschmidt partner, a literal id ('hapagedi', …) for an internal one. */
  key: string;
  label: string;
  group: EdiInvoiceBucketGroup;
  billtos: number[];
  count: number;
  /** Why this bucket is narrowed the way it is — shown as the row's tooltip. */
  note: string;
}

export interface EdiInvoiceBucketsResponse {
  data: EdiInvoiceBucket[];
  total: number;
  groups: EdiInvoiceBucketGroup[];
}

/** One invoice number in a bucket. Several `invoice` rows fold into one of these, which
 *  is why pronumbers/bol/po are lists or comma-joined. */
export interface EdiInvoiceRow {
  invoicenumber: string;
  invhistoryrecno: number;
  invoicedate: string;
  billto: number;
  billtoname: string;
  mas90code: string;
  bol: string;
  po: string;
  movetype: string;
  billtorefno: string;
  invoicetotal: number;
  /** 'Y' when Accounting has flagged this invoice to go out again. */
  resend: string;
  pronumbers: number[];
  /** How many invoices are folded in — what the "bill separately" rules count. */
  invoicecount: number;
  haschassismanagement: boolean;
  errors: number;
  errormessages: string[];
  /** Rule ids behind `errormessages`, same order — for grouping/filtering. */
  errorrules: string[];
}

export interface EdiInvoiceResultsResponse {
  data: EdiInvoiceRow[];
  total: number;
  bucket: { key: string; label: string; group: EdiInvoiceBucketGroup; note: string };
  /** Storage invoices ('ST…') the drill drops but the bucket count includes — V1's own
   *  count-vs-list gap, surfaced instead of left as a silent discrepancy. */
  storage_excluded: number;
  /** Validation rules that could not be evaluated because an input was not loaded.
   *  Always empty in normal operation; non-empty means the list is UNDER-validated. */
  skipped_rules: string[];
  totalamount: number;
  totalclean: number;
}

export async function getInvoiceBuckets(): Promise<EdiInvoiceBucketsResponse> {
  const res = await api.get('/pending-edi-invoices/buckets');
  return res.data;
}

/** One bucket's validation state — how much of it would be refused if sent now. */
export interface EdiBucketValidation {
  key: string;
  invoices: number;
  errors: number;
  /** Rules that could not be evaluated for this bucket. Non-empty means the bucket is
   *  UNDER-validated, so a clean result there is weaker than it looks. */
  skippedRules: string[];
  /** Non-empty when the sweep failed for this bucket. It is NOT clean — it is unknown. */
  error: string;
}

export interface EdiBucketValidationResponse {
  data: EdiBucketValidation[];
  totalErrors: number;
  /** When the sweep ran. It is cached and slow, so this can lag the counts. */
  checkedAt: string;
}

/**
 * Validate every bucket, so the list can say WHERE the problems are.
 *
 * SLOW AND CACHED — it validates every pending invoice (measured ~16s cold on prod,
 * served from a 60s server-side cache after that). Never block the bucket list on it:
 * fetch the counts first, paint, then colour when this lands.
 */
export async function getBucketValidation(): Promise<EdiBucketValidationResponse> {
  const res = await api.get('/pending-edi-invoices/validation');
  return res.data;
}

export async function getInvoiceBucketResults(bucket: string): Promise<EdiInvoiceResultsResponse> {
  const res = await api.get('/pending-edi-invoices/results', { params: { bucket } });
  return res.data;
}

/** `$1,234.56`, the format the V1 screen totalled in. */
export const money = (n: number): string =>
  `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** One invoice the Remove action wrote, or refused to. */
export interface EdiRemoveOutcome {
  invhistoryrecno: number;
  invoicenumber?: string;
  pronumbers?: number[];
  /** Only on `skipped` — why the server refused this one. */
  reason?: string;
}

export interface EdiRemoveResponse {
  removed: EdiRemoveOutcome[];
  skipped: EdiRemoveOutcome[];
  notes_written: number;
  bucket: { key: string; label: string };
}

/**
 * "Remove EDI" — mark these invoices donotsend='Y' so they leave the bucket and never go
 * out over EDI. Ports V1's Remove EDI button (invoiceedi.pl::donotsendEDI).
 *
 * Partial success is normal: the server checks each invoice again (resend flag, already
 * removed, already sent, belongs to this bucket) and reports `removed` and `skipped`
 * separately. Callers must show both — a silent skip is how an invoice quietly stays in
 * the queue.
 */
export async function removeFromEdi(
  bucket: string,
  invhistoryrecnos: number[],
): Promise<EdiRemoveResponse> {
  const res = await api.post('/pending-edi-invoices/remove', { bucket, invhistoryrecnos });
  return res.data;
}

/** One file a send produced. A partner batched into several files gets several. */
export interface EdiSendFile {
  filename: string;
  controlNumber: string;
  invoiceCount: number;
  sizeBytes: number;
  /** False when the file was built and archived but deliberately not transmitted. */
  transmitted: boolean;
  /** Always populated — why it was or was not sent. */
  transmitReason: string;
  /** Non-empty when this file failed; the others in the batch may still have gone. */
  error: string;
}

/** An invoice validation refused to send, with the same reasons the Errors column shows. */
export interface EdiSendBlocked {
  invoicenumber: string;
  invhistoryrecno: number;
  reasons: string[];
  skippedRules: string[];
}

/**
 * One Intelligent Audit paperwork bundle — the delivery order, POD, EIRs and invoice for
 * one pro, merged into the PDF the invoice row names. Only At Home and Office Depot get
 * these; every other partner sends an empty list.
 */
export interface EdiSendBundle {
  invoicenumber: string;
  filename: string;
  pageCount: number;
  sizeBytes: number;
  transmitted: boolean;
  /** Documents that would not merge, by type — the bundle went out without them. */
  skippedDocuments: string[];
  /** Non-empty when this bundle failed. The invoice file itself had already gone. */
  error: string;
}

export interface EdiSendResponse {
  bucket: { key: string; label: string };
  files: EdiSendFile[];
  /** invoice_history recnos that actually went out. */
  sent: number[];
  blocked: EdiSendBlocked[];
  /** Line items with no charge code for this partner — those invoices were held back. */
  unmappedLineItems: string[];
  notesWritten: number;
  /** Empty for every partner but the two Intelligent Audit ones. */
  bundles?: EdiSendBundle[];
}

/**
 * "Send EDI" — build the Kleinschmidt invoice file for these invoices, archive it,
 * transmit it, and mark them sent.
 *
 * BLOCKS until the file has gone. That is deliberate: V1 locks the page and refreshes
 * with the sent rows gone, and an optimistic toast that lands before the FTP does would
 * tell the operator something the system does not yet know.
 *
 * Partial success is normal and must be shown in full — a batched partner can have one
 * file fail while others land, invoices can be blocked by validation, and a test system
 * builds the file without transmitting. Only `sent` actually left.
 */
export async function sendEdi(
  bucket: string,
  invhistoryrecnos: number[],
): Promise<EdiSendResponse> {
  const res = await api.post('/pending-edi-invoices/send', { bucket, invhistoryrecnos });
  return res.data;
}

export interface EdiPreviewFile {
  filename: string;
  /** The complete file, exactly as a send would transmit it. */
  body: string;
  invoiceCount: number;
}

export interface EdiPreviewResponse {
  bucket: { key: string; label: string };
  files: EdiPreviewFile[];
  blocked: EdiSendBlocked[];
  unmappedLineItems: string[];
}

/**
 * Build the invoice file for these invoices and hand it back WITHOUT sending it.
 *
 * Writes nothing — no control number is claimed, nothing is archived, no invoice is
 * marked — so it can be run as often as you like and always gives the same answer. The
 * filename differs from a real send's (that one carries the control number); the content
 * is identical, which is what a comparison against V1 cares about.
 */
export async function previewEdi(
  bucket: string,
  invhistoryrecnos: number[],
  includeBlocked = false,
): Promise<EdiPreviewResponse> {
  const res = await api.post('/pending-edi-invoices/preview', {
    bucket, invhistoryrecnos, includeBlocked,
  });
  return res.data;
}

/**
 * Save a built file to the user's machine.
 *
 * BOM-free and latin-1-safe: the EDI file is plain ASCII and must stay that way, so
 * nothing here re-encodes it. The type follows the extension — a Kleinschmidt file is a
 * CSV, an internal partner's X12 210 is not, and labelling a .edi as text/csv makes
 * Excel offer to open it.
 *
 * Goes through `saveBlob` so it honours Settings → Downloads → Show Save As like every
 * other download in the app. It used to click its own hidden anchor, which always went
 * straight to the Downloads folder no matter what the user had chosen.
 *
 * Resolves false when the user cancelled the Save As dialog.
 */
export function downloadEdiFile(file: EdiPreviewFile): Promise<boolean> {
  const csv = /\.csv$/i.test(file.filename);
  const blob = new Blob([file.body], {
    type: csv ? 'text/csv;charset=us-ascii' : 'text/plain;charset=us-ascii',
  });
  return saveBlob(blob, file.filename);
}
